import { $host } from './index';
import { AppDispatch } from '@store';
import { isSpinAC, isSwitchRouteLoadingAC, notificationModalAC, userAC } from '@store/reducers/app';
import { markNotificationsReadAC, notificationsAC, permissionsAC, templatesAC } from '@store/reducers/user';
import openNotification from '@utils/openNotification';

const END_POINT = '/client';

const getUserInfoApi = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const { data } = await $host.get(`${END_POINT}/me`);
    dispatch(userAC(data));
  } finally {
    dispatch(isSpinAC(false));
  }
};

const getNotificationsApi = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const { data } = await $host.get(`${END_POINT}/notifications/`);
    dispatch(notificationsAC(data));
  } finally {
    dispatch(isSpinAC(false));
  }
};

const markNotificationsReadApi = (notificationIds: number[]) => async (dispatch: AppDispatch) => {
  if (!notificationIds.length) return;
  dispatch(markNotificationsReadAC(notificationIds));
  try {
    await $host.put(`${END_POINT}/notifications/mark-read`, { notification_ids: notificationIds });
  } catch (error) {
    dispatch(getNotificationsApi());
  }
};

const getTemplatesApi = (params: object) => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const { data } = await $host.post(`${END_POINT}/transaction/templates`, params);
    dispatch(templatesAC(data));
  } finally {
    dispatch(isSpinAC(false));
  }
};

const getTemplatesDownloadApi = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));

    const { data } = await $host.get(`${END_POINT}/transaction/template/download`, {
      responseType: 'arraybuffer',
    });

    const url = window.URL.createObjectURL(new Blob([data], { type: 'application/xlsx' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Шаблоны.xlsx');
    link.click();

    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    dispatch(
      notificationModalAC({
        isOpen: true,
        message: error?.response?.data?.detail || error?.response?.data?.message || 'Ошибка скачивания файла',
        type: 'error',
      })
    );
  } finally {
    dispatch(isSpinAC(false));
  }
};

const deleteTemplateApi = (params: object, type: any) => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    await $host.post(`${END_POINT}/transaction/template/delete`, params);
    dispatch(getTemplatesApi({ transaction_type: type }));
  } catch (error: any) {
    dispatch(
      notificationModalAC({
        isOpen: true,
        message: error?.response?.data?.detail || error?.response?.data?.message,
        type: 'error',
      })
    );
  } finally {
    dispatch(isSpinAC(false));
  }
};

const saveTemplateApi = (params: object) => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    await $host.post(`${END_POINT}/transaction/template`, params);
    openNotification('success', { description: 'Шаблон успешно создан!' });
  } catch (error: any) {
    dispatch(
      notificationModalAC({
        isOpen: true,
        message: error?.response?.data?.detail || error?.response?.data?.message,
        type: 'error',
      })
    );
  } finally {
    dispatch(isSpinAC(false));
  }
};

const updateContactInfoApi = (params: object) => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const { data } = await $host.put(`${END_POINT}/contact-info/update`, params);
    return data;
  } catch (error: any) {
    dispatch(
      notificationModalAC({
        isOpen: true,
        message: error?.response?.data?.detail || error?.response?.data?.message,
        type: 'error',
      })
    );
  } finally {
    dispatch(isSpinAC(false));
  }
};

const updateContactInfoVerifyApi = (params: object, callback: any) => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    await $host.put(`${END_POINT}/contact-info/update/verify`, params);
    await dispatch(getUserInfoApi());
    callback();
    dispatch(
      notificationModalAC({
        isOpen: true,
        message: 'Данные успешно изменены!',
        type: 'success',
      })
    );
  } catch (error: any) {
    dispatch(
      notificationModalAC({
        isOpen: true,
        message: error?.response?.data?.detail || error?.response?.data?.message,
        type: 'error',
      })
    );
  } finally {
    dispatch(isSpinAC(false));
  }
};

const updateContactInfoResendCodeApi = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const { data } = await $host.post(`${END_POINT}/contact-info/update/verify/resend-code`);
    return data;
  } catch (error: any) {
    dispatch(
      notificationModalAC({
        isOpen: true,
        message: error?.response?.data?.detail || error?.response?.data?.message,
        type: 'error',
      })
    );
  } finally {
    dispatch(isSpinAC(false));
  }
};

const getUserPermissionsApi = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const { data } = await $host.get(`${END_POINT}/permissions/me`);
    dispatch(permissionsAC(data));
    dispatch(isSwitchRouteLoadingAC(false));
  } finally {
    dispatch(isSpinAC(false));
  }
};

const sendFeedbackApi = (params: any) => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    const { data } = await $host.post(`${END_POINT}/feedback`, params);
    return data;
  } finally {
    dispatch(isSpinAC(false));
  }
};

const getGoogleQrCodeAdminApi =
  () =>
  async (dispatch: AppDispatch): Promise<string | null> => {
    try {
      dispatch(isSpinAC(true));
      const { data } = await $host.get(`${END_POINT}/google-auth/qrcode`);
      return data as string;
    } catch (e: any) {
      dispatch(
        notificationModalAC({
          isOpen: true,
          message: e.response?.data?.detail || 'Произошла ошибка',
          type: 'error',
        })
      );
      return null;
    } finally {
      dispatch(isSpinAC(false));
    }
  };

const toggleGoogleAuthApi = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(isSpinAC(true));
    await $host.put(`${END_POINT}/google-auth`);
    await dispatch(getUserInfoApi());
  } catch (e: any) {
    dispatch(
      notificationModalAC({
        isOpen: true,
        message: e.response?.data?.detail || 'Произошла ошибка',
        type: 'error',
      })
    );
  } finally {
    dispatch(isSpinAC(false));
  }
};

export {
  getGoogleQrCodeAdminApi,
  toggleGoogleAuthApi,
  sendFeedbackApi,
  getTemplatesApi,
  saveTemplateApi,
  getUserInfoApi,
  getUserPermissionsApi,
  deleteTemplateApi,
  getTemplatesDownloadApi,
  getNotificationsApi,
  markNotificationsReadApi,
  updateContactInfoApi,
  updateContactInfoVerifyApi,
  updateContactInfoResendCodeApi,
};
